import React, { useRef, useState } from "react";
import { HomeOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Input, Space, Table } from "antd";
import "./data_wh_transfer_in.css";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from "swr";
import axios from "axios";
import { useNavigate } from "react-router-dom";

interface DataType {
    key: string;
    transferInNo: string;
    date: string;
    warehouseName: string;
    warehouseCode: string;
    status: number;
    transferOutNo: string;
}

type DataIndex = keyof DataType;

const fetcher = (url: string) => axios.get(url).then((res) => res.data);

const statusMap: { [key: number]: string } = {
    1: 'Draft',
    2: 'Accept',
    3: 'Ready',
    4: 'Reject',
    5: 'Closed',
    6: 'intransit',
};

const getStatusLabel = (status: number): string => {
    return statusMap[status] || 'Unknown';
};

const DataWhTransferIn: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const navigate = useNavigate();

    const { data, error } = useSWR(
        `${process.env.REACT_APP_BACKEND_URL}/api/wh-transfer-in-data`,
        fetcher
    );

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<DataType> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) =>
            (dataIndex === 'status' ? getStatusLabel(record.status) : record[dataIndex].toString())
                .toLowerCase()
                .includes((value as string).toLowerCase()),
        onFilterDropdownOpenChange: (visible) => {
            if (visible) {
                setTimeout(() => searchInput.current?.select(), 100);
            }
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const parseDate = (dateStr: string) => {
        const [day, month, year] = dateStr.split('/');
        return new Date(`${year}-${month}-${day}`).getTime();
    };

    const columns: TableColumnsType<DataType> = [
        {
            title: 'Transfer In No',
            dataIndex: 'transferInNo',
            key: 'transferInNo',
            ...getColumnSearchProps('transferInNo'),
            sorter: (a, b) => a.transferInNo.localeCompare(b.transferInNo),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Date',
            dataIndex: 'date',
            key: 'date',
            ...getColumnSearchProps('date'),
            sorter: (a, b) => parseDate(a.date) - parseDate(b.date),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Warehouse Name',
            dataIndex: 'warehouseName',
            key: 'warehouseName',
            ...getColumnSearchProps('warehouseName'),
            sorter: (a, b) => a.warehouseName.localeCompare(b.warehouseName),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Warehouse Code',
            dataIndex: 'warehouseCode',
            key: 'warehouseCode',
            ...getColumnSearchProps('warehouseCode'),
            sorter: (a, b) => a.warehouseCode.localeCompare(b.warehouseCode),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            ...getColumnSearchProps('status'),
            render: (status: number) => getStatusLabel(status),
            sorter: (a, b) => getStatusLabel(a.status).localeCompare(getStatusLabel(b.status)),
            sortDirections: ['descend', 'ascend'],
        },
        {
            title: 'Transfer Out No',
            dataIndex: 'transferOutNo',
            key: 'transferOutNo',
            ...getColumnSearchProps('transferOutNo'),
            sorter: (a, b) => a.transferOutNo.localeCompare(b.transferOutNo),
            sortDirections: ['descend', 'ascend'],
        },
    ];

    if (error) return <div>Failed to load data.</div>;

    const tableData: DataType[] = data?.map((item: any) => ({
        key: item.trans_in_no,
        transferInNo: item.trans_in_no,
        date: item.trans_in_date,
        warehouseName: item.warehouse_name_from,
        warehouseCode: item.warehouse_code_from,
        status: Number(item.status),
        transferOutNo: item.trans_out_no,
    })) || [];

    return (

        <div id="iiok" style={transformStyle}>
            <div className="gjs-grid-row" id="i9bk">
                <div className="gjs-grid-column" id="ifff">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data WH Transfer In</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/item_wh_transfer_in",
                                title: "Item WH Transfer In",
                            },
                        ]}
                    />
                </div>
            </div>

            <div className="gjs-grid-row" id="ietfl">
                <div className="gjs-grid-column" id="isgj">
                    <Table<DataType>
                        columns={columns}
                        dataSource={tableData}
                        loading={!data && !error}
                        scroll={{ x: 1300 }}
                        onRow={(record) => {
                            return {
                                onDoubleClick: () => {
                                    navigate('/dashboard/item_wh_transfer_in', { state: { trans_in_no: record.transferInNo } });
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default DataWhTransferIn;